import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../core/providers/AuthProvider';
import { useCases } from '../../../core/di/useCases';

// Définition des étapes déplacée ici car c'est une constante qui ne change pas
const ONBOARDING_STEPS = [
  'welcome',
  'phone',
  'birthday',
  'activities',
  'location',
  'photo',
  'completion'
];

export const useOnboardingState = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [onboardingData, setOnboardingData] = useState({});

  const loadOnboardingState = useCallback(async () => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      if (!useCases?.getUserProfile?.execute) {
        console.error('getUserProfile n\'est pas disponible');
        return;
      }

      const userData = await useCases.getUserProfile.execute(user.uid);
      
      // Restaurer les données d'onboarding
      const userOnboardingData = userData?.onboardingData || {};
      setOnboardingData(userOnboardingData);
      
      // Restaurer l'étape actuelle avec vérification de validation des étapes précédentes
      if (userData?.onboardingStep) {
        const targetStepIndex = ONBOARDING_STEPS.indexOf(userData.onboardingStep);
        
        // Si l'étape sauvegardée est "phone" mais que la vérification du téléphone est réussie,
        // nous devrions aller directement à l'étape suivante (birthday)
        if (userData.onboardingStep === 'phone' && userOnboardingData.phoneVerified === true) {
          // Aller à l'étape suivante (birthday)
          const nextStepIndex = targetStepIndex + 1;
          setCurrentStep(nextStepIndex >= 0 && nextStepIndex < ONBOARDING_STEPS.length ? nextStepIndex : 0);
        } else {
          // Comportement normal
          setCurrentStep(targetStepIndex >= 0 && targetStepIndex < ONBOARDING_STEPS.length ? targetStepIndex : 0);
        }
      }
    } catch (error) {
      console.error('Erreur chargement onboarding:', error);
      setCurrentStep(0);
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  const saveStepData = async (stepId, stepData) => {
    if (!user?.uid || !useCases?.updateOnboardingStep?.execute) return false;

    try {
      const updatedData = {
        ...onboardingData,
        ...stepData
      };

      await useCases.updateOnboardingStep.execute(user.uid, stepId, {
        onboardingData: updatedData,
        onboardingStep: stepId
      });

      setOnboardingData(updatedData);
      return true;
    } catch (error) {
      console.error('Erreur sauvegarde étape:', error);
      return false;
    }
  };

  // Ajout d'une vérification de sécurité pour currentStepId
  const getCurrentStepId = useCallback(() => {
    return ONBOARDING_STEPS[currentStep] || ONBOARDING_STEPS[0];
  }, [currentStep]);

  useEffect(() => {
    if (useCases?.getUserProfile) {
      loadOnboardingState();
    }
  }, [loadOnboardingState]);

  return {
    loading,
    currentStep,
    setCurrentStep,
    onboardingData,
    saveStepData,
    reload: loadOnboardingState,
    currentStepId: getCurrentStepId()
  };
};

export { ONBOARDING_STEPS }; 